import React from 'react';
import { styled, useTheme } from '@mui/material/styles';
import { darken, lighten, alpha, Typography } from "@mui/material";
import { deepmerge } from '@mui/utils';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import List from '@mui/material/List';
import IconButton from '@mui/material/IconButton';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Collapse from '@mui/material/Collapse';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import Drawer from '@mui/material/Drawer';
import Bs4Popper from '../Bs4Components/Bs4Popper';
import { v4 as uuidv4 } from 'uuid';
import { Resizable } from 're-resizable';
import Bs4Kartoteka from '../Bs4Kartoteka';
import Bs4MenuGear from './Bs4MenuGear';
import Bs4MainDispatch from '../Bs4MainDispatch';
import Bs4MainWindow, {TYPE_VERTICALMENU, TYPE_VERTICALMENU_OPTIONS} from '../Bs4MainWindow';
import { getBoxSx, SmallScreenContext, useStateUxTemplate } from '../Bs4Theme/Bs4UxTemplates';
import Bs4Icon from '../Bs4Components/Bs4Icon'
import { shallowEqual } from '../Bs4Components/Bs4Utils'

export const MENU_TYPE_LEFT = 'left';

const getDefoultUxTemplateInit = (theme) => {
  return {
    divider: false,
    box: {
      color: theme.palette.primary.contrastText,
      backgroundColor: theme.palette.primary.main,
      borderRadius: '0px '+(theme.shape.borderRadius * 2)+'px '+(theme.shape.borderRadius * 2)+'px 0px',
      borderRadiusLeft: `0px ${theme.shape.borderRadius * 2}px ${theme.shape.borderRadius * 2}px 0px`,
      borderRadiusRight: `${theme.shape.borderRadius * 2}px 0px  0px ${theme.shape.borderRadius * 2}px`,
    },    
  };
}

const getDefoultUxTemplateInitOptions = (theme) => {
  return {
    divider: false,
    box: {
      color: theme.palette.primary.contrastText,
      backgroundColor: theme.palette.primary.main,
      borderRadius: (theme.shape.borderRadius * 2)+'px ',
    },    
  };
}

const getColor = (uxTemplate, alphaF) => {
  return alpha(uxTemplate.box.color, alphaF);
}

const getItems = (parent, items, level) => {  
  if (!Array.isArray(items)) return null;
  return items.map(item => {
    item.parent = parent;
    if (item.items) item.items = getItems(item, item.items, level + 1);
    item.key = uuidv4();
    return item;
  })
}

const ListStyled = styled(List)(({ uxtemplate }) => ({
  color: getColor(uxtemplate, uxtemplate.box.color),
  backgroundColor: uxtemplate.box.backgroundColor,  
  ':hover': {
    backgroundColor: uxtemplate.box.backgroundColor,
  },
}));

const ListItemStyled = styled(ListItem)(({ uxtemplate }) => ({
  color: getColor(uxtemplate, uxtemplate.box.color),
  backgroundColor: uxtemplate.box.backgroundColor,
  "&.Mui-selected": {
    color: getColor(uxtemplate, uxtemplate.text.selected),
    backgroundColor: uxtemplate.box.backgroundColor,
    ':hover': {
      color: getColor(uxtemplate, uxtemplate.text.selected),
      backgroundColor: uxtemplate.box.backgroundColor,
    },
  },  
  ':hover': {
    color: getColor(uxtemplate, uxtemplate.text.hover),
    backgroundColor: uxtemplate.box.backgroundColor,
  },
}));

const ListItemButtonStyled = styled(ListItemButton)(({ uxtemplate }) => ({
  color: getColor(uxtemplate, uxtemplate.text.main),
  backgroundColor: 'transparent',
  "&.Mui-selected": {
    color: getColor(uxtemplate, uxtemplate.text.selected),
    backgroundColor: 'transparent',
  },
  "&.Mui-selected:hover": {
    color: getColor(uxtemplate, uxtemplate.text.selected),
    backgroundColor: 'transparent',
  },
  ':hover': {
    color: getColor(uxtemplate, uxtemplate.text.hover),
    backgroundColor: 'transparent',
  },
}));

const IconButtonStyled = styled(IconButton)(({ uxtemplate }) => ({  
  color: getColor(uxtemplate, uxtemplate.text.main),
  backgroundColor: 'transparent',
  "&.Mui-selected": {
    color: getColor(uxtemplate, uxtemplate.text.selected),
    backgroundColor: 'transparent',
  },  
  ':hover': {
    color: getColor(uxtemplate, uxtemplate.text.hover),
    backgroundColor: 'transparent',
  },
}));

const BoxScrollbarStyled = styled(Box)(({ uxtemplate }) => ({  
  '&::-webkit-scrollbar': {
    width: uxtemplate.scrollbar.width,
  },
  '&::-webkit-scrollbar-track': {
    background: uxtemplate.scrollbar.trackBackground,
    borderRadius: uxtemplate.scrollbar.trackBorderRadius,
  },
  '&::-webkit-scrollbar-track:hover': {
    background: uxtemplate.scrollbar.trackBackgroundHover,
    borderRadius: uxtemplate.scrollbar.trackBorderRadius,
  },
  '&::-webkit-scrollbar-thumb': {
    background: uxtemplate.scrollbar.thumbBackground,
    borderRadius: uxtemplate.scrollbar.trackBorderRadius,
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: uxtemplate.scrollbar.thumbBackgroundHover,
    borderRadius: uxtemplate.scrollbar.trackBorderRadius,
  }
}));

const isEqualId = (id, item) => {    
  if (item.bs4Id && String(item.bs4Id) === String(id)) return true;
  if (item.bs4FolderId && String(item.bs4FolderId) === String(id)) return true;
  return false
}

const hesItemOptionId = (topItem, optionId) => {
  if (parseInt(topItem.bs4Id) === parseInt(optionId)) return true;
  if (Array.isArray(topItem.items)) for (const item of topItem.items) {
    if (hesItemOptionId(item, optionId)) return true;
  }

  return false;
};

const ItemMenu = (props) => {
  const {item, level, onClickItem, onClickExpand, open, getItemTitle, showTooltip, ...other} = props
  const paddingLeft = level * 3;
  const refItemMenu = React.useRef();
  const theme = useTheme();
  if (!item.iconCls) item.iconCls = ''; //"fa-regular fa-period" 

  const selected = React.useMemo(() => {
      return hesItemOptionId(item, props.optionId);
    }, [props.optionId, item])

  const boxSx = React.useMemo(() => getBoxSx(item?.bs4CustomCss, theme), []);
  const uxTemplate = React.useMemo(() => {
    const uxTemplate = JSON.parse(JSON.stringify(other.uxTemplate));
    uxTemplate.box = deepmerge(uxTemplate.box, boxSx);
    return uxTemplate;
  }, [other.uxTemplate]) 

  const handleClickMenu = (event) => {
    onClickItem(item)
  }

  React.useEffect(() => {    
    if (showTooltip && isEqualId(showTooltip?.id, item)) {      
      showTooltip.showTooltip(refItemMenu, showTooltip.closeShowTooltipMenu);
    }
  }, [showTooltip, refItemMenu.current])

  return (
    <React.Fragment>
      <ListItemStyled
        id={item.key}
        ref={refItemMenu}
        sx={{
          pl: paddingLeft
          // , border: 'solid'
        }} 
        dense 
        disableGutters 
        divider={Boolean(uxTemplate.divider)}
        uxtemplate={uxTemplate}
      >
        <ListItemButtonStyled disableGutters onClick={handleClickMenu} selected={selected} uxtemplate={uxTemplate}>
          <ListItemIcon 
            sx={{
              justifyContent:'center', 
              color: 'inherit', 
              //backgroundColor: 'inherit'
            }}
          >
            <Bs4Icon iconCls={item.iconCls} iconStyle={item.iconStyle}/>
          </ListItemIcon>
          <ListItemText primary={getItemTitle(item)}/>
          <Bs4MenuGear
            item={item} 
            bs4ConstructorMode={props.bs4ConstructorMode} 
            bs4KartotekaId={props.bs4KartotekaId}
            targetOption={props.targetOption}
          />
        </ListItemButtonStyled>
        {item.items 
          ?
          <IconButtonStyled onClick={() => onClickExpand(item)} uxtemplate={uxTemplate}>
            {open.get(item) ? <ExpandLess/> :  <ExpandMore/>}
          </IconButtonStyled> 
          :
          <IconButtonStyled uxtemplate={uxTemplate}>            
          </IconButtonStyled> 
        }
      </ListItemStyled>
      {item.items
        ?
        <Collapse in={open.get(item)} timeout="auto" unmountOnExit>
          <ListStyled uxtemplate={uxTemplate}>
            {item.items.map((itemChild) => 
              <ItemMenu 
                key={itemChild.key}
                item={itemChild} 
                level={level + 1} 
                onClickItem={onClickItem} 
                selected={selected} 
                onClickExpand={onClickExpand} 
                open={open} 
                getItemTitle={getItemTitle}                
                uxTemplate={uxTemplate}    
                showTooltip={showTooltip}
                optionId={props.optionId}
                bs4KartotekaId={props.bs4KartotekaId}
                bs4ConstructorMode={props.bs4ConstructorMode}
                targetOption={props.targetOption}
              />
            )}
          </ListStyled>
        </Collapse>
        :
        null
      }
    </React.Fragment>
  )
};

const getItemMenu = (id, items) => {
  if (!items) return null;
  for (const item of items) {
    if (String(item.bs4Id) === String(id)) return item;
    const returnedItem = getItemMenu(id, item.items);
    if (returnedItem) return returnedItem;
  }
}

const openItemMenu = (item, open) => {  
  if (!item) return;
  if (item.parent) {
    openItemMenu(item.parent, open);
    open.push([item, true]);
  }
}

const testItemMenu = (items) => {
  //const newItems = JSON.parse(JSON.stringify(items));
  items[0].items = JSON.parse(JSON.stringify(items));;
  return items;
}

const Bs4VerticalMenuContent = (props) => {  
  const [open, setOpen] = React.useState(new Map());
  const [showTooltip, setShowTooltip] = React.useState(null);
  const [delayedTabsData, setDelayedTabsData] = React.useState({});
  const [optionId, setOptionId] = React.useState(null);
  const menu = React.useMemo(() => {        
    const menu = props.menu;    
    menu.showTooltip = (id, showTooltip, closeShowTooltip) => {      
      const item = getItemMenu(id, menu.items);
      if (!item) return;
      if (props.setOpenDrawer) props.setOpenDrawer(true);
      const open = []
      openItemMenu(item.parent, open);
      setOpen(new Map(open));
      menu.setShowTooltip = 
      setShowTooltip({
        id,
        showTooltip,        
        closeShowTooltip,
        closeShowTooltipMenu: () => {
          setShowTooltip(null);
          delete menu.closeShowTooltip;
          if (props.setOpenDrawer) props.setOpenDrawer(false);
        }
      });
      menu.closeShowTooltip = () => {
        closeShowTooltip();
        delete menu.closeShowTooltip;
        setShowTooltip(null);
      }
    };    
    menu.setOptionId = setOptionId;
    return menu;
  }, [open, props.menu, props.setOpenDrawer, getItemMenu, setOptionId, setOpen]);

  const theme = useTheme();

  const bs4ConstructorMode = React.useMemo(() => {
      const bs4Kartoteka = Bs4Kartoteka.get(Bs4Kartoteka.firstBs4KartotekaId);
      return bs4Kartoteka?.message?._Komunikat?.bs4ConstructorMode;
    }, []);

  const boxSx = React.useMemo(() => {
    const boxSx = getBoxSx(props.uxTemplate.box, theme) 
    if (props.drawerOrPoper) {
      delete boxSx.borderRadius;
      delete boxSx.boxShadow;
      delete boxSx.margin;
    }
    return boxSx;
  }, [theme, props.dateTimeStamp, props.drawerOrPoper, props.uxTemplate?.box]);

  // const smallScreen = React.useContext(SmallScreenContext);
  
  const debug = false;
  const getItemTitle = (item) => {
    const string = delayedTabsData[item.bs4Id];
    let text = item.text || '';
    if (debug) {
      if (item.bs4FolderId) text = item.bs4FolderId+':F:'+text;
      else if (item.bs4Id)  text = text = item.bs4Id+':I:'+text;
      else text = '???:'+text;
    }
    if (string) return text ? text+' '+string : string;
    return text;
  }

  const closeAbove = (item) => {
    item?.items?.forEach(itemChild => {
      closeAbove(itemChild);
      if (open.get(itemChild)) open.delete(itemChild); 
    });    
  }

  const closeBelow = (item) => {
    if (!item) return;
    item?.parent?.items?.forEach(itemChild => {
      if (item !== itemChild && open.get(itemChild)) open.delete(itemChild);      
    });
    closeBelow(item?.parent);
  }

  const handleClickExpand = (item) => {    
    const openState = !open.get(item);
    
    closeAbove(item);
    closeBelow(item);

    setOpen(new Map([...Array.from(open), [item, openState]]));
  }

  const handleClickItem = (item) => {
    if (!item.bs4Id) {
      handleClickExpand(item);
      return;
    }

    if (Bs4MainDispatch.isCommandParserWaitForServer()) return;

    closeAbove(item);
    closeBelow(item);

    // setOpen(new Map([...Array.from(open), [item, false]]));
    // setSelected(item)

    if (props.onMenuClick) props.onMenuClick(item);
  }

  React.useEffect(() => {
    if (props.addDelayedTabsData) {
      const bs4Kartoteka = Bs4Kartoteka.get(props.bs4KartotekaId);
      if (bs4Kartoteka) {
        bs4Kartoteka.addDelayedTabsData = (message) => setDelayedTabsData(message._Komunikat)
        Bs4MainDispatch.commandParser('Bs4LoadCounter', {currCardViewId: bs4Kartoteka.bs4KartotekaId}, bs4Kartoteka.bs4KartotekaId);
      }      
    }
  }, [props.bs4KartotekaId])

  React.useEffect(() => {
    if (props.addDelayedTabsData) {
      const bs4Kartoteka = Bs4Kartoteka.get(props.bs4KartotekaId);
      if (bs4Kartoteka) bs4Kartoteka.updateDelayedTabsData = (message) => {
        setDelayedTabsData({ ...delayedTabsData, ...message._Komunikat})
      }
    }
  }, [delayedTabsData])

  React.useEffect(() => {
    if (menu.waitingShowTooltip && menu.showTooltip) {
      menu.showTooltip(...menu.waitingShowTooltip);
      delete menu.waitingShowTooltip;
    }
  }, [menu]);

  const refSpeedTest = React.useRef({
    shallowEqualValues: null,
    shallowEqualProps: null,
  });
  Bs4MainDispatch.speedRun((tag) => {
    const newObj = {
      open,
      showTooltip, 
      delayedTabsData, 
      optionId,
      menu,      
    };
    console.log(Bs4MainDispatch.speedNextTime(tag), props.debugText || '', shallowEqual(newObj, refSpeedTest.current.shallowEqualValues, Bs4MainDispatch.speedPrefix+tag+' EqualValues'), shallowEqual(props, refSpeedTest.current.shallowEqualProps, Bs4MainDispatch.speedPrefix+tag+' EqualProps'));
    refSpeedTest.current.shallowEqualValues = newObj;
    refSpeedTest.current.shallowEqualProps = props;
  }, '..MenuV..Content');       

  return (    
    <Box height='100%' display='flex' flexDirection='column'>
      <Box 
        sx={{
          flex: 1, 
          overflow: 'hidden',                     
          ...boxSx,
          // border: 'solid',
        }}
      >
        <BoxScrollbarStyled height='100%' overflow='auto' uxtemplate={props.uxTemplate}>         
          <List subheader={null}>
            {menu.items.map((item) => 
              <ItemMenu 
                key={item.key}
                item={item} 
                level={0} 
                onClickItem={handleClickItem} 
                onClickExpand={handleClickExpand}
                open={open} 
                getItemTitle={getItemTitle}
                boxSx={boxSx}
                uxTemplate={props.uxTemplate}
                showTooltip={showTooltip}
                optionId={optionId}
                bs4KartotekaId={props.bs4KartotekaId}
                bs4ConstructorMode={bs4ConstructorMode}
                targetOption={props.targetOption}
              />
            )}
          </List>      
        </BoxScrollbarStyled>
      </Box>
    </Box>      
  )
}

const getMenu = (props) => {
  const menu = {parent: null};
  menu.canShowTooltip = id => getItemMenu(id, menu.items);
  menu.showTooltip = (id, showTooltip, closeShowTooltip) => {     
    menu.waitingShowTooltip = [id, showTooltip, closeShowTooltip];
  };
  menu.items = JSON.stringify(props?.bs4Menu);
  menu.items = menu.items ? JSON.parse(menu.items) : [];    
  menu.items = getItems(menu, menu.items, 0) || [];
  
  if (menu.items.length === 1 && !menu.items[0].bs4Id) {
    menu.items = menu.items[0].items;
  }
  
  if (props?.setMenu) props.setMenu(menu);
  return menu;
}

export const Bs4VerticalMenu = (props) => {
  if (props.bs4MenuType !== MENU_TYPE_LEFT) {
    return null;
  }
  return (
    <Bs4VerticalMenuLeft {...props}/>
  )
}

export const Bs4VerticalMenuLeft = (props) => {
  const key = 'bs4MainWindowLeftMenuWidth';
  const [width, setWidth] = React.useState(parseFloat(window.localStorage.getItem(key)) || 300);
  const smallScreen = React.useContext(SmallScreenContext);
  const menu = React.useMemo(() => {
    return getMenu(props);
  }, [props.bs4Menu]);
  const [MenuIconButton, setMenuIconButton] = React.useState(null);
  const setMenuIconButtonProps = React.useCallback((MenuIconButton) => {
    if (props.setMenuIconButton) {
      props.setMenuIconButton(MenuIconButton);
      setMenuIconButton(null);
    } else {
      setMenuIconButton(MenuIconButton);
    }
  }, [smallScreen, props.setMenuIconButton, setMenuIconButton]);

  const theme = useTheme();
  const {dateTimeStamp} = useStateUxTemplate(TYPE_VERTICALMENU);
  const uxTemplate = React.useMemo(() => {
    const defaultUx = getDefoultUxTemplateInit(theme);
    let uxTemplate = Bs4MainWindow.instance?.bs4MainWindowUx?.uxTemplate?.body?.VerticalMenu;
    uxTemplate = deepmerge(defaultUx, uxTemplate);
    return uxTemplate;
  }, [theme, dateTimeStamp])

  const handleResizeStop = (e, direction, ref, d) => {
    const newWidth = Math.max(30, width + d.width);
    setWidth(newWidth)      
    window.localStorage.setItem(props.key || key, String(newWidth));
  }

  if (! Array.isArray(props?.bs4Menu) || props?.bs4Menu.length < 1) {
    return null;
  }  

  if (smallScreen || props.miniLeftMenu) {
    return (
    <React.Fragment>
      {MenuIconButton}
      <Bs4VerticalMenuDrawer {...props} anchor='left' setMenuIconButton={setMenuIconButtonProps}/>
    </React.Fragment> 
  )};

  return (
    <Resizable 
      size={{ width: width, height: '100%' }}
      onResizeStop={ handleResizeStop }
      enable={{top:false, right:true, bottom:false, left:false, topRight:false, bottomRight: false, bottomLeft: false, topLeft: false}}
    >
      <Bs4VerticalMenuContent {...props} menu={menu} uxTemplate={uxTemplate}/>
    </Resizable>
  );
}

const MenuIconButton = React.forwardRef((props, ref) => {
  const smallScreen = React.useContext(SmallScreenContext);
  const handleOpenDrawer = (event) => {
    if (props.setOpenDrawer) props.setOpenDrawer(true)
  } 

  const icon = React.useMemo(() => {
    if (props.startIcon) return <props.startIcon/>;

    switch (props.variantMenuIcon) {
      case 'dot':
      case 'popper':
        return <i className="fa-solid fa-ellipsis-vertical" style={{paddingLeft: '4px', paddingRight: '4px'}}/>    ;
  
      case 'ellipsis-stroke':
        return <i className="fa-regular fa-ellipsis-stroke"/>;
  
      default: 
        return <i className="fa-solid fa-bars"/>;
    }  
  }, [props.variantMenuIcon, props.startIcon])
  
  const alignItems = 'center';//props.anchor === 'left' ? 'flex-start' : 'flex-end';

  if (smallScreen || props.miniLeftMenu) {
    return (      
      <IconButton
        ref={ref}
        // edge="start"
        onClick={handleOpenDrawer}
        // sx={{paddingLeft: 2}}
      >
        <Stack direction="column" justifyContent="center" alignItems={alignItems}>
          {icon}
          {props.variantMenuName ? <Typography variant='caption'>{props.variantMenuName}</Typography> : null}
        </Stack>
      </IconButton>       
    );
  }

  return null;  
});

export const Bs4VerticalMenuDrawer = (props) => {
  const [openDrawer, setOpenDrawer] = React.useState(false);  
  const ref = React.useRef(null)
  const theme = useTheme();
  const {dateTimeStamp} = useStateUxTemplate(TYPE_VERTICALMENU);
  const menu = React.useMemo(() => getMenu(props), [getMenu]);
  const uxTemplate = React.useMemo(() => {
    const defaultUx = getDefoultUxTemplateInit(theme);
    let uxTemplate = Bs4MainWindow.instance?.bs4MainWindowUx?.uxTemplate?.body?.VerticalMenu;
    uxTemplate = deepmerge(defaultUx, uxTemplate);
    return uxTemplate;
  }, [theme, dateTimeStamp])

  const handleMenuClick = React.useCallback((item) => {    
    if (Bs4MainDispatch.isCommandParserWaitForServer()) return;
    if (props.onMenuClick) props.onMenuClick(item);
    setOpenDrawer(false);
  }, [props.onMenuClick]);

  React.useEffect(() => {
    if (props.setMenuIconButton) props.setMenuIconButton(<MenuIconButton ref={ref} setOpenDrawer={setOpenDrawer} {...props}/>);
    return () => {
      if (props.setMenuIconButton) props.setMenuIconButton(null);
    }
  }, [props.setMenuIconButton, setOpenDrawer]);

  React.useEffect(() => {
    if (!openDrawer && menu.closeShowTooltip) menu.closeShowTooltip();
  }, [openDrawer, menu]);  

  return (
    <Drawer
      keepMounted
      anchor={props.anchor}
      open={openDrawer}
      // onOpen={(event) => setOpenDrawer(true)}        
      onClose={(event) => setOpenDrawer(false)}
      slotProps={{poper: {sx: {backgroundColor: '#00000000'}}}}
    >
      <Bs4VerticalMenuContent {...props} menu={menu} onMenuClick={handleMenuClick} drawerOrPoper setOpenDrawer={setOpenDrawer} uxTemplate={uxTemplate}/>
    </Drawer>
  )
}

export const Bs4VerticalMenuPopper = (props) => {  
  const [openPopper, setOpenPopper] = React.useState(false);
  const ref = React.useRef(null)
  const menu = React.useMemo(() => getMenu(props), [getMenu]);  
  
  const handleMenuClick = (item) => {    
    if (Bs4MainDispatch.isCommandParserWaitForServer()) return;
    if (props.onMenuClick) props.onMenuClick(item);
    setOpenPopper(false);
  }

  // console.log('...Bs4ShowTooltip...Bs4VerticalMenuPopper', props);

  const theme = useTheme();
  const {dateTimeStamp} = useStateUxTemplate(TYPE_VERTICALMENU_OPTIONS);
  const uxTemplate = React.useMemo(() => {
    const defaultUx = getDefoultUxTemplateInitOptions(theme);
    const uxTemplate = Bs4MainWindow.instance?.bs4MainWindowUx?.uxTemplate?.body?.VerticalMenuOptions;
    const uxTemplate1 = deepmerge(defaultUx, uxTemplate);
    return uxTemplate1;
  }, [theme, dateTimeStamp])

  const boxSx = React.useMemo(() => {
    return getBoxSx(uxTemplate.box, theme);
  }, [theme, dateTimeStamp]);

  React.useEffect(() => {
    if (props.setMenuIconButton) props.setMenuIconButton(<MenuIconButton ref={ref} setOpenDrawer={setOpenPopper} {...props}/>);
    return () => {
      if (props.setMenuIconButton) props.setMenuIconButton(null);
    }
  }, [props.setMenuIconButton, setOpenPopper]);

  React.useEffect(() => {
    if (!openPopper && menu.closeShowTooltip) menu.closeShowTooltip();
  }, [openPopper, menu]);  
  
  return (
    <Bs4Popper
      anchorEl={ref.current}
      keepMounted
      open={openPopper} 
      onClose={(event) => setOpenPopper(false)} 
      bs4kartotekaid={props.bs4KartotekaId}
      // placement='bottom'
      placement='bottom-start'  
      slotProps={{
        sx: {
          overflow: 'hidden',
          maxWidth: '80vw',            
          ...boxSx,
          //backgroundColor: '#ff0000',
        }
      }}
    >
      <Bs4VerticalMenuContent {...props} menu={menu} onMenuClick={handleMenuClick} drawerOrPoper setOpenDrawer={setOpenPopper} uxTemplate={uxTemplate}/>
    </Bs4Popper>  
  )
}

export default Bs4VerticalMenu;